import { httpGetWithCsrfToken } from '../utils/Http';
import { keysToCamelFromSnake } from '../utils/caseconverters';
import { aggregateHeatmapItems } from '../utils/heatmap';

// Filter params travel from backend/editor as a list of {key, value} pairs.
// Convert that list to URLSearchParams via [key, value] tuples — every pair
// becomes one query parameter so repeated keys (e.g. multiple __ne rows in
// the future) are preserved.
const buildFenixSearchParams = (filterPairs) => {
    if (!filterPairs) return new URLSearchParams();
    const tuples = filterPairs
        .filter((p) => p && p.key)
        .map((p) => [p.key, p.value == null ? '' : String(p.value)]);
    return new URLSearchParams(tuples);
};

export const fetchFenixEditorialContent = async (isoCode, contentType) => {
    const url = `/wt/fenix/editorial-content/?isoCode=${encodeURIComponent(isoCode)}`;
    console.log('[Fenix API] Fetching editorial content:', url, contentType);

    const response = await httpGetWithCsrfToken(url);

    if (!response.ok) {
        return { ok: false };
    }

    const data = await response.json();
    const items =
        data.items || data.results || (Array.isArray(data) ? data : [data]);

    const country = items.find((c) => c.isoCode === isoCode) || null;
    if (!country) {
        return { ok: false };
    }

    const editorialContents = country.editorialContents || [];
    const entry = editorialContents.find((e) => e.type === contentType);

    if (!entry) {
        return { ok: true, html: null, publishedAt: null, publisher: null };
    }

    return {
        ok: true,
        html: entry.html || entry.content || '',
        publishedAt: entry.publishedAt || null,
        publisher: entry.publisher || null,
    };
};

export const fetchFenixChartData = async (dataset, filterParams) => {
    const params = buildFenixSearchParams(filterParams);
    // Load every row, not just the backend's default first page, so the
    // published chart shows the full dataset (matching the editor preview).
    params.set('fetch_all', 'true');
    const url = `/wt/fenix/graphs/data/${dataset}/?${params.toString()}`;
    console.log('[Fenix API] Fetching:', url);

    const response = await httpGetWithCsrfToken(url);

    if (!response.ok) {
        return response;
    }

    return response;
};

export const fetchFenixDatasetFirstItem = async (
    dataset,
    filterParams,
    { orderByProperty, orderByDirection } = {}
) => {
    const params = buildFenixSearchParams(filterParams);
    if (orderByProperty) {
        params.set('order_by', orderByProperty);
        params.set('order_direction', orderByDirection || 'desc');
    }
    // Single-value only needs the top row — upstream honours sort+sort_dir
    // so page_size=1 gives us exactly what we want without paginating.
    params.set('page_size', '1');
    const url = `/wt/fenix/graphs/data/${dataset}/?${params.toString()}`;

    const response = await httpGetWithCsrfToken(url);

    if (!response.ok) {
        return { ok: false };
    }

    const data = await response.json();
    const items = data.items || [];
    const item = items[0] || null;

    return { ok: true, item };
};

export const fetchFenixEntitiesAggregate = async (filterParams) => {
    const params = buildFenixSearchParams(filterParams);
    const url = `/wt/fenix/entities/aggregate/?${params.toString()}`;

    const response = await httpGetWithCsrfToken(url);

    if (!response.ok) {
        return { ok: false };
    }

    const data = await response.json();
    const items = data.items || [];
    const item = items[0] || null;

    return { ok: true, item };
};

const CONNECTION_ENTITY_FIELDS = [
    'source_id',
    'name',
    'country_code',
    'is_person',
    'person_key_role',
    'global_ultimate_owner',
    'category',
    'revenue',
    'employee_count',
].join(',');

const ENTITIES_PAGE_SIZE = 500;
const PERSONS_PAGE_SIZE = 100;
const MAX_PAGES = 40;

export const fetchFenixEntities = async (filterParams = {}) => {
    const items = [];

    for (let page = 1; page <= MAX_PAGES; page++) {
        const params = new URLSearchParams({
            ...filterParams,
            fields: CONNECTION_ENTITY_FIELDS,
            page_size: String(ENTITIES_PAGE_SIZE),
            page: String(page),
        });

        const response = await httpGetWithCsrfToken(
            `/wt/fenix/entities/?${params.toString()}`
        );

        if (!response.ok) {
            return { ok: false, items: keysToCamelFromSnake(items) };
        }

        const data = await response.json();
        const pageItems = data.items || [];
        items.push(...pageItems);

        if (pageItems.length < ENTITIES_PAGE_SIZE) {
            break;
        }
    }

    return { ok: true, items: keysToCamelFromSnake(items) };
};

export const fetchFenixPersonsForCountry = async (countryCode) => {
    const items = [];

    for (let page = 1; page <= MAX_PAGES; page++) {
        const params = new URLSearchParams({
            country: countryCode.toUpperCase(),
            pageSize: String(PERSONS_PAGE_SIZE),
            page: String(page),
        });

        const response = await httpGetWithCsrfToken(
            `/wt/fenix/persons/?${params.toString()}`
        );

        if (!response.ok) {
            return { ok: false, items };
        }

        const data = await response.json();
        items.push(...(data.items || []));

        if (!data.pageInfo?.hasNextPage) {
            break;
        }
    }

    return { ok: true, items };
};

export const fetchFenixOrgChart = async (holdingId) => {
    const params = new URLSearchParams({ holding: holdingId });
    const response = await httpGetWithCsrfToken(
        `/wt/fenix/connections/org_chart/?${params.toString()}`
    );

    if (!response.ok) {
        return { ok: false, nodes: [] };
    }

    const data = await response.json();

    return { ok: true, nodes: data.data || [] };
};

export const fetchFenixHeatMapChartData = async (
    dataset,
    filterParams,
    serie
) => {
    const params = buildFenixSearchParams(filterParams);
    params.set('fetch_all', 'true');
    const url = `/wt/fenix/graphs/data/${dataset}/?${params.toString()}`;
    console.log('[Fenix API] Fetching heatmap:', url);

    const response = await httpGetWithCsrfToken(url);

    if (!response.ok) {
        return response;
    }

    const data = await response.json();
    const items = data.items || [];

    const {
        xAxisCategories,
        yAxisCategories,
        data: heatmapData,
    } = aggregateHeatmapItems(items, {
        property: serie.property,
        xAxisCategoryProperty: serie.xAxisCategoryProperty,
        yAxisCategoryProperty: serie.yAxisCategoryProperty,
    });

    return {
        ok: true,
        json: async () => ({
            xAxisCategories,
            yAxisCategories,
            data: heatmapData,
        }),
    };
};

const getHierarchyField = (level) =>
    level?.groupingProperty ||
    level?.grouping_property ||
    level?.ontologyProperty ||
    level?.ontology_property;

const getHierarchyLabelField = (level) =>
    level?.labelProperty || level?.label_property;

const getTreemapNodeId = (parts) => JSON.stringify(parts.map(String));

export const formatFenixTreeMapItems = (items, serie, hierarchyLevels = []) => {
    const valueField = serie?.property || 'value';
    const levels = hierarchyLevels
        .map((level) => ({
            field: getHierarchyField(level),
            labelField: getHierarchyLabelField(level),
        }))
        .filter((level) => level.field);

    const nodesById = new Map();

    items.forEach((item) => {
        const value = parseFloat(item[valueField]);

        if (!Number.isFinite(value)) {
            return;
        }

        const rowHierarchy = levels.map((level) => item[level.field]);
        const rowHasCompleteHierarchy = rowHierarchy.every(
            (groupingValue) =>
                groupingValue !== null &&
                typeof groupingValue !== 'undefined' &&
                groupingValue !== ''
        );

        if (!rowHasCompleteHierarchy) {
            return;
        }

        const pathParts = [];

        for (const [levelIndex, level] of levels.entries()) {
            const groupingValue = rowHierarchy[levelIndex];
            pathParts.push(groupingValue);

            const id = getTreemapNodeId(pathParts);
            const parent =
                levelIndex > 0
                    ? getTreemapNodeId(pathParts.slice(0, -1))
                    : null;
            const labelValue = level.labelField
                ? item[level.labelField]
                : groupingValue;

            const existingNode = nodesById.get(id);

            if (existingNode) {
                if (levelIndex === levels.length - 1) {
                    existingNode.value += value;
                    existingNode.colorValue += value;
                }
                continue;
            }

            nodesById.set(id, {
                id,
                parent,
                name: String(labelValue || groupingValue),
                value: levelIndex === levels.length - 1 ? value : undefined,
                grouping: {
                    key: level.field,
                    value: String(groupingValue),
                },
                colorValue:
                    levelIndex === levels.length - 1 ? value : undefined,
            });
        }
    });

    return Array.from(nodesById.values());
};

export const fetchFenixTreeMapChartData = async (
    dataset,
    filterParams,
    serie,
    hierarchyLevels
) => {
    const params = buildFenixSearchParams(filterParams);
    params.set('fetch_all', 'true');

    const url = `/wt/fenix/graphs/data/${dataset}/?${params.toString()}`;
    console.log('[Fenix API] Fetching treemap:', url);

    const response = await httpGetWithCsrfToken(url);

    if (!response.ok) {
        return response;
    }

    const data = keysToCamelFromSnake(await response.json(), false);
    const items = data.items || [];

    return {
        ok: true,
        json: async () => ({
            treemap: formatFenixTreeMapItems(items, serie, hierarchyLevels),
        }),
    };
};

export const fetchFenixTimelineChartData = async (
    dataset,
    filterParams,
    serie
) => {
    const params = buildFenixSearchParams(filterParams);
    params.set('fetch_all', 'true');
    const url = `/wt/fenix/graphs/data/${dataset}/?${params.toString()}`;
    console.log('[Fenix API] Fetching timeline:', url);

    const response = await httpGetWithCsrfToken(url);

    if (!response.ok) {
        return response;
    }

    const data = await response.json();
    const items = data.items || [];

    const dateField = serie.dateProperty || 'date';
    const titleField = serie.titleProperty || 'title';
    const descField = serie.descriptionProperty || 'description';

    const serieData = items.map((item) => ({
        x: item[dateField],
        name: item[titleField],
        description: item[descField],
    }));

    return {
        ok: true,
        json: async () => ({
            serie: {
                data: serieData,
            },
        }),
    };
};
